import ApiRouteProvider from '../route'
import axios from '@axios'
import Cache from "@core/api/request/Cache";
import {SubstanceLists} from "@/libs/substance";
import {objectToGETParams} from "../route";

export default {
    getAttributes() {
        return Cache.item('ProductAttributes', () => {
            return axios.get(ApiRouteProvider.product.attributes());
        }).getPromise();
    },
    getCategory(id) {
        return Cache.item('ProductCategory_' + id, () => {
            return axios.get(ApiRouteProvider.product.category(id));
        }).getPromise();
    },
    getCategories(qs) {
        if (undefined === qs) {
            return Cache.item('AllProductCategories', () => {
                return axios.get(ApiRouteProvider.product.categories(), null);
            }).getPromise();
        }

        return axios.get(ApiRouteProvider.product.categories(), qs ? {params: {qs: qs}} : null);
    },
    getSendRequestEmailTemplates() {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/send-request-email/template');
    },
    getCategoryChildren(id) {
        return axios.get(ApiRouteProvider.product.categoryChildren(id));
    },

    searchPurchasedProducts(supplierId, qs) {
        return axios.get(ApiRouteProvider.product.searchPurchasedProducts(supplierId, qs));
    },

    searchIncludedProducts(qs) {
        return axios.get(ApiRouteProvider.product.searchIncludedProducts(qs));
    },

    getGroups() {
        return axios.get(ApiRouteProvider.product.groups());
    },

    getProductsForProductOverview(page, perPage) {
        return axios.get(ApiRouteProvider.product.overview({
            page,
            perPage
        }));
    },

    productOverviewFilter(filters, page, perPage) {
        return axios.get(ApiRouteProvider.product.overviewFilter(filters, page, perPage))
    },

    add(product) {
        return axios.post(ApiRouteProvider.product.add(), product);
    },

    makeScipSubmission(id) {
        return axios.post(ApiRouteProvider.product.makeSubmission(id), {
            type: 'SCIP'
        });
    },

    getProductCard(id) {
        return axios.get(ApiRouteProvider.product.card(id));
    },

    getProduct(id) {
        return axios.get(ApiRouteProvider.product.product(id));
    },

    getChildren(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/children');
    },

    getSupplierDocuments(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/supplier-documents');
    },

    getSCIPNotificationFacts(id) {
        return axios.get(ApiRouteProvider.product.scipNotificationFacts(id));
    },

    getSCIPNotificationPreview(id) {
        return axios.get(ApiRouteProvider.product.scipNotificationPreview(id));
    },

    getHierarchy(id) {
        return axios.get(ApiRouteProvider.product.hierarchy(id));
    },

    getSentRequests(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getSentRequests({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSentRequest(id) {
        return axios.get(ApiRouteProvider.product.getSentRequest(id));
    },

    saveExternalResponse(id, response) {
        return axios.post(
            ApiRouteProvider.getBaseUrl() + '/product/sent/requests/' + id + '/external-response',
            response
        );
    },

    getForwardedRequests(receivedRequestId) {
        return axios.get(ApiRouteProvider.product.getCustomerRequestSentRequests(receivedRequestId));
    },

    getCustomerRequest(id) {
        return axios.get(ApiRouteProvider.product.getCustomerRequest(id));
    },

    updateCustomerRequest(id, request) {
        return axios.put(ApiRouteProvider.product.updateCustomerRequest(id), request);
    },

    getCustomerRequests(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getCustomerRequests({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSubmissions(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getSubmissions({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSubmissionCard(id) {
        return axios.get(ApiRouteProvider.product.getSubmissionCard(id));
    },

    getComponentsToRequest(id) {
        return axios.get(ApiRouteProvider.product.componentsToRequest(id));
    },

    getConcernElements(id) {
        return axios.get(ApiRouteProvider.product.getConcernElements(id));
    },

    includeComponents(id, components) {
        return axios.post(ApiRouteProvider.product.includeComponents(id), {components: components});
    },

    updateComponent(id, component) {
        return axios.post(ApiRouteProvider.product.updateComponent(id, component.id), {
            numberOfUnits: component.numberOfUnits
        });
    },

    excludeComponent(id, componentId) {
        return axios.delete(ApiRouteProvider.product.excludeComponent(id, componentId));
    },

    convertType(id, type) {
        return axios.post(ApiRouteProvider.product.convertType(id), {type});
    },

    update(id, product) {
        return axios.put(ApiRouteProvider.product.update(id), product);
    },

    updateScipSettings(id, settings) {
        return axios.put(ApiRouteProvider.product.updateScipSettings(id), settings);
    },

    excludeFromScipNotification(id, product, parent) {
        return axios.post(ApiRouteProvider.product.excludeFromScipNotification(id), {
            product, parent
        })
    },

    unsetExclusionFromScipNotification(id, product, parent) {
        return axios.delete(ApiRouteProvider.product.unsetExclusionFromScipNotification(id, product, parent))
    },

    getSubstances(id) {
        return axios.get(ApiRouteProvider.product.getSubstances(id));
    },

    sendRequest(id, request) {
        return axios.post(ApiRouteProvider.product.sendRequest(id), request);
    },

    approveRequest(id) {
        return axios.post(ApiRouteProvider.product.approveRequest(id), {
            requests: [parseInt(id)]
        });
    },
    partiallyApproveRequest(id) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/sent/requests/approve-partially', {
            requests: [parseInt(id)]
        });
    },

    rejectRequest(id, reason) {
        return axios.post(ApiRouteProvider.product.rejectRequest(id), {
            reason: reason
        });
    },

    forwardRequest(requestId, request) {
        return axios.post(ApiRouteProvider.product.forwardRequest(requestId), request);
    },

    getSubstanceState(id, legislations = []) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/substance-state?' + objectToGETParams({
            legislations: legislations
        }));
    },

    replyToRequest(requestId, declaration) {
        let declarationValidity = null;
        if (declaration.declarationValidity) {
            declarationValidity = Number.isInteger(declaration.declarationValidity) ? declaration.declarationValidity : declaration.declarationValidity.month;
        }
        return axios.post(ApiRouteProvider.product.replyRequest(requestId), {
            documents: declaration.documents.filter(d => d.id).map(d => d.id),
            internalComment: declaration.internalComment,
            customerInformation: declaration.customerInformation,
            declarationValidity: declarationValidity
        });
    },

    getReply(requestId) {
        return axios.get(ApiRouteProvider.product.getReply(requestId));
    },

    saveSubstances(id, included, excluded) {
        return axios.put(ApiRouteProvider.product.saveSubstances(id), {
            included: included.map(substance => {
                let request = {
                    id: substance.substance.id,
                    details: {}
                };
                if (substance.details) {
                    let getId = (index, item, listKey) => {
                        return substance.details[listKey].details[index][item] ? substance.details[listKey].details[index][item].id : null;
                    };
                    if (substance.details.hasOwnProperty(SubstanceLists.CANDIDATE_LIST)) {
                        request.details[SubstanceLists.CANDIDATE_LIST] = substance.details[SubstanceLists.CANDIDATE_LIST].details.map((s, i) => {
                            return {
                                concentrationRangeId: getId(i, 'concentrationRange', SubstanceLists.CANDIDATE_LIST),
                                additionalMaterialId: getId(i, 'additionalMaterial', SubstanceLists.CANDIDATE_LIST),
                                materialCategoryId: getId(i, 'materialCategory', SubstanceLists.CANDIDATE_LIST),
                                mixtureCategoryId: getId(i, 'mixtureCategory', SubstanceLists.CANDIDATE_LIST),
                                maxConcentrationRange: s.maxConcentration || null,
                            };
                        });
                        request.details[SubstanceLists.CANDIDATE_LIST][0]['exemption'] = substance.details[SubstanceLists.CANDIDATE_LIST].details[0].exemption;
                    }
                    if (substance.details.hasOwnProperty(SubstanceLists.ROHS)) {
                        if (substance.details[SubstanceLists.ROHS].details.length) {
                            request.details[SubstanceLists.ROHS] = [{
                                exemption: substance.details[SubstanceLists.ROHS].details[0].exemption,
                                concentrationRangeId: getId(0, 'concentrationRange', SubstanceLists.ROHS),
                                maxConcentrationRange: substance.details[SubstanceLists.ROHS].details[0].maxConcentration || null
                            }];
                        }
                    }
                    if (substance.details.hasOwnProperty(SubstanceLists.POPS) && substance.details[SubstanceLists.POPS].details.length) {
                        request.details[SubstanceLists.POPS] = [{
                            exemption: substance.details[SubstanceLists.POPS].details[0].exemption,
                            maxConcentrationRange: substance.details[SubstanceLists.POPS].details[0].maxConcentration || null
                        }];
                    }
                    if (substance.details.hasOwnProperty(SubstanceLists.FMV) && substance.details[SubstanceLists.FMV].details.length) {
                        request.details[SubstanceLists.FMV] = [{
                            exemption: substance.details[SubstanceLists.FMV].details[0].exemption,
                            maxConcentrationRange: substance.details[SubstanceLists.FMV].details[0].maxConcentration || null
                        }];
                    }
                    Object.keys(substance.details).forEach(key => {
                        if (!request.details.hasOwnProperty(key)) {
                            request.details[key] = [{
                                exemption: substance.details[key].details[0].exemption,
                                maxConcentrationRange: substance.details[key].details[0].maxConcentration || null
                            }];
                        }
                    });
                }
                return request;
            }),
            excluded: excluded,
        });
    },

    buildFormData(formData, data, parentKey) {
        if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
            Object.keys(data).forEach(key => {
                this.buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
            });
        } else {
            const value = data == null ? '' : data;

            formData.append(parentKey, value);
        }
    },

    getDocuments(id) {
        return axios.get(ApiRouteProvider.product.getDocuments(id));
    },

    deleteDocument(pid, id) {
        return axios.delete(ApiRouteProvider.product.deleteDocument(pid, id));
    },

    downloadDocument(pid, id) {
        return axios({
            url: ApiRouteProvider.product.downloadDocument(pid, id),
            method: 'GET',
            responseType: 'blob',
        });
    },

    downloadRepliedDocument(requestId, id) {
        return axios({
            url: ApiRouteProvider.getBaseUrl() + '/product/sent/requests/' + requestId + '/reply/documents/' + id + '/download',
            method: 'GET',
            responseType: 'blob',
        });
    },

    remove(id) {
        return axios.delete(ApiRouteProvider.product.remove(id));
    },

    saveDocuments(
        id,
        DoC,
        evidenceReports,
        compliantStatements,
        safeUseInstructions,
        dissemblingInstructions,
        isSafeUseApplicable,
        productDescription
    ) {
        //  todo refactor this
        const formData = new FormData();
        const safeUse = {
            description: safeUseInstructions.description
        };

        const dissembling = {
            description: dissemblingInstructions.description
        };

        if (safeUseInstructions.id) {
            safeUse.id = safeUseInstructions.id;
        }

        if (safeUseInstructions.document instanceof File) {
            safeUse.document = safeUseInstructions.document;
        }

        if (dissemblingInstructions.id) {
            dissembling.id = dissemblingInstructions.id;
        }

        if (dissemblingInstructions.document instanceof File) {
            dissembling.document = dissemblingInstructions.document;
        }

        const data = {
            declarationOfConformity: DoC.map(doc => {
                const document = {
                    legislations: doc.legislations.map(l => l.id),
                    declarationDate: doc.declarationDate,
                    declarationValidityDate: doc.declarationValidityDate,
                };

                if (doc.document instanceof File) {
                    document.document = doc.document;
                }

                if (doc.id) {
                    document.id = doc.id;
                }

                return document;
            }),
            compliantStatement: compliantStatements.map(doc => {
                const document = {
                    legislations: doc.legislations.map(l => l.id),
                    declarationDate: doc.declarationDate,
                    declarationValidityDate: doc.declarationValidityDate,
                };

                if (doc.document instanceof File) {
                    document.document = doc.document;
                }

                if (doc.id) {
                    document.id = doc.id;
                }

                return document;
            }),
            evidenceReports: evidenceReports.map(report => {
                const document = {
                    legislations: report.legislations.map(l => l.id),
                    documentTypes: report.documentTypes.map(t => t.id),
                };

                if (report.document instanceof File) {
                    document.document = report.document;
                }

                if (report.id) {
                    document.id = report.id;
                }

                return document;
            }),
        };

        if (safeUse.id || safeUse.description || safeUse.document) {
            data.safeUseInstructions = safeUse;
        }

        if (dissembling.id || dissembling.description || dissembling.document) {
            data.dissemblingInstructions = dissembling;
        }

        data.isSafeUseApplicable = isSafeUseApplicable ? 1 : 0;

        if (productDescription && productDescription.document) {
            const productDescriptionData = {
                document: productDescription.document
            };

            if (productDescription.id) {
                productDescriptionData.id = productDescription.id;
            }

            data.description = productDescriptionData;
        }

        this.buildFormData(formData, data);

        return axios.post(ApiRouteProvider.product.saveDocuments(id), formData);
    },

    componentsSubstances(id, legislations) {
        if (legislations) {
            legislations = legislations.map(l => l.id);
        }
        return axios.get(ApiRouteProvider.product.componentsSubstances(id, legislations));
    },

    additionalMaterials() {
        return Cache.item('ProductAdditionalMaterials', () => {
            return axios.get(ApiRouteProvider.product.additionalMaterials());
        }).getPromise();
    },

    mixtureCategories() {
        return Cache.item('ProductMixtureCategories', () => {
            return axios.get(ApiRouteProvider.product.mixtureCategories());
        }).getPromise();
    },

    sendProductRequestEmail(productId) {
        return axios.post(ApiRouteProvider.product.sendProductRequestEmail(productId), {
            productId
        });
    },

    resendRequestEmail(productRequestId) {
        return axios.get(ApiRouteProvider.product.mixtureCategories());
    },

    sendApproveDeclarationMessage(method, productRequestId, name, options) {
        return axios.post(ApiRouteProvider.product.sendApproveDeclarationMessage(productRequestId), {
            method, name, options
        });
    },

    approveDeclaration(productRequestId, code) {
        return axios.post(ApiRouteProvider.product.approveDeclaration(productRequestId), {
            code
        });
    },

    getGeneralProductStatements(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements?' + objectToGETParams({
            filters,
            sort: sorting,
            page,
            perPage
        }))
    },

    getAllGeneralProductStatements() {
        return Cache.item('GeneralProductStatements', () => {
            return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements?' + objectToGETParams({
                page: 1,
                perPage: 256
            }))
        }).getPromise();
    },

    getGeneralProductStatement(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements/' + id);
    },

    createGeneralProductStatement(data) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements', data);
    },

    updateGeneralProductStatement(id, data) {
        return axios.put(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements/' + id, data);
    }
};
